import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, X } from "lucide-react";

export type ItemMulti = { id: string; label: string; secundario?: string };

/**
 * Filtro de MÚLTIPLA escolha (jul/2026, pedido da Thainá): "quero ver arte
 * digital e impressa juntas" / "selecionar mais de um colaborador". Os filtros
 * da tela de tarefas guardavam UM valor cada; este componente é a versão lista.
 *
 * Fecha no clique fora e no ESC — é popover de filtro, não formulário: o único
 * conteúdo que se perde é o termo de busca. (A regra "só fecha no X" da Rec vale
 * pra tela onde se digita conteúdo — ver hooks/useFecharComEsc.)
 */
export function MultiSelectFiltro({
  items,
  values,
  onChange,
  placeholder,
  nomePlural,
  className = "",
}: {
  items: ItemMulti[];
  values: string[];
  onChange: (ids: string[]) => void;
  /** Texto quando nada está marcado. Ex.: "Todos os tipos" */
  placeholder: string;
  /** Usado no resumo quando há 2+. Ex.: "tipos" → "3 tipos" */
  nomePlural: string;
  className?: string;
}) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState("");
  const caixaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!aberto) return;
    const clicouFora = (e: MouseEvent) => {
      if (caixaRef.current && !caixaRef.current.contains(e.target as Node)) {
        setAberto(false);
        setBusca("");
      }
    };
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setAberto(false);
        setBusca("");
      }
    };
    document.addEventListener("mousedown", clicouFora);
    document.addEventListener("keydown", aoTeclar, true);
    return () => {
      document.removeEventListener("mousedown", clicouFora);
      document.removeEventListener("keydown", aoTeclar, true);
    };
  }, [aberto]);

  const visiveis = useMemo(() => {
    const t = busca.trim().toLowerCase();
    if (!t) return items;
    return items.filter(
      (i) => i.label.toLowerCase().includes(t) || (i.secundario || "").toLowerCase().includes(t),
    );
  }, [items, busca]);

  // Resumo no botão: 1 marcado mostra o nome; 2+ mostra a contagem (o nome de
  // todos estouraria a largura do filtro).
  const resumo =
    values.length === 0
      ? placeholder
      : values.length === 1
        ? items.find((i) => i.id === values[0])?.label || `1 ${nomePlural}`
        : `${values.length} ${nomePlural}`;

  const alternar = (id: string) =>
    onChange(values.includes(id) ? values.filter((v) => v !== id) : [...values, id]);

  return (
    <div ref={caixaRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => { setAberto((v) => !v); setBusca(""); }}
        className={
          "w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border text-sm outline-none transition-colors " +
          (values.length > 0
            ? "border-blue-400 bg-blue-50 text-blue-800 dark:bg-blue-900/20 dark:text-blue-200 dark:border-blue-700"
            : "border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-slate-600 dark:text-slate-300")
        }
      >
        <span className="truncate">{resumo}</span>
        <span className="flex items-center gap-1 shrink-0">
          {values.length > 0 && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); onChange([]); }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onChange([]); } }}
              className="hover:text-red-600 cursor-pointer"
              title="Limpar"
            >
              <X size={13} />
            </span>
          )}
          <ChevronDown size={14} className="text-slate-400" />
        </span>
      </button>

      {aberto && (
        <div className="absolute z-30 mt-1 w-full min-w-[220px] rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg overflow-hidden">
          {items.length > 6 && (
            <div className="p-2 border-b border-slate-100 dark:border-slate-700">
              <input
                type="text"
                autoFocus
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Buscar…"
                className="w-full px-2.5 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 text-sm outline-none focus:border-blue-500"
              />
            </div>
          )}

          <div className="max-h-60 overflow-y-auto py-1">
            {visiveis.length === 0 && (
              <p className="px-3 py-2 text-xs text-slate-400">Sem resultados.</p>
            )}
            {visiveis.map((i) => {
              const marcado = values.includes(i.id);
              return (
                <button
                  key={i.id}
                  type="button"
                  onClick={() => alternar(i.id)}
                  className="w-full px-3 py-1.5 text-left text-sm flex items-center gap-2 hover:bg-slate-50 dark:hover:bg-slate-700/50"
                >
                  <span
                    className={
                      "w-4 h-4 rounded border flex items-center justify-center shrink-0 " +
                      (marcado
                        ? "bg-blue-600 border-blue-600 text-white"
                        : "border-slate-300 dark:border-slate-600")
                    }
                  >
                    {marcado && <Check size={11} strokeWidth={3} />}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block truncate text-slate-700 dark:text-slate-200">{i.label}</span>
                    {i.secundario && (
                      <span className="block truncate text-[10px] text-slate-400">{i.secundario}</span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>

          {values.length > 0 && (
            <div className="border-t border-slate-100 dark:border-slate-700 px-3 py-1.5">
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-[11px] text-slate-500 hover:text-red-600"
              >
                Limpar seleção ({values.length})
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
